import React, { useEffect, useMemo, useState } from 'react';
import { Button, Empty, Input, List, Modal, Popconfirm, Typography } from 'antd';
import AddBookedItemListRoster from './AddBookedItemListRoster';
import DeleteRosterTemplate from './DeleteRosterTemplate';
import FetchRosterTemplates from './FetchRosterTemplates';
import SaveRosterTemplate from './SaveRosterTemplate';
import {
  buildManualTemplatePayload,
  expandManualTemplateRules,
} from './templateUtils';

const { Text } = Typography;

const RosterTemplateModal = ({
  open,
  onClose,
  token,
  companyId,
  locationId,
  bookedItems,
  currentWeekRange,
  onApplied,
}) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [applyStartDate, setApplyStartDate] = useState(currentWeekRange?.startDate || '');
  const [applyEndDate, setApplyEndDate] = useState(currentWeekRange?.endDate || '');

  useEffect(() => {
    setApplyStartDate(currentWeekRange?.startDate || '');
    setApplyEndDate(currentWeekRange?.endDate || '');
  }, [currentWeekRange?.startDate, currentWeekRange?.endDate]);

  const loadTemplates = async () => {
    if (!open || !companyId || !locationId || !token) {
      return;
    }

    setLoading(true);
    const response = await FetchRosterTemplates(companyId, locationId, token);
    setTemplates(response);
    setLoading(false);
  };

  useEffect(() => {
    loadTemplates();
  }, [open, companyId, locationId, token]);

  const previewCounts = useMemo(() => {
    return templates.reduce((accumulator, template) => {
      accumulator[template.id] = expandManualTemplateRules({
        templateRules: template.templateRules,
        startDate: applyStartDate,
        endDate: applyEndDate,
        companyId,
        locationId,
      }).length;
      return accumulator;
    }, {});
  }, [applyEndDate, applyStartDate, companyId, locationId, templates]);

  const handleCreate = async () => {
    if (!templateName.trim()) {
      return;
    }

    setSaving(true);
    try {
      const payload = buildManualTemplatePayload({
        templateName,
        companyId,
        locationId,
        bookedItems,
        startDate: currentWeekRange?.startDate,
        endDate: currentWeekRange?.endDate,
      });
      await SaveRosterTemplate(payload, token);
      setTemplateName('');
      await loadTemplates();
    } finally {
      setSaving(false);
    }
  };

  const handleApply = async (template) => {
    const generatedItems = expandManualTemplateRules({
      templateRules: template.templateRules,
      startDate: applyStartDate,
      endDate: applyEndDate,
      companyId,
      locationId,
    });

    if (generatedItems.length === 0) {
      return;
    }

    setSaving(true);
    try {
      await AddBookedItemListRoster(generatedItems, token);
      onApplied(generatedItems);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    setSaving(true);
    try {
      await DeleteRosterTemplate(id, token);
      await loadTemplates();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onCancel={onClose} footer={null} width={720} title="Roster templates">
      <div style={{ display: 'grid', gap: 16 }}>
        <div style={{ display: 'grid', gap: 8 }}>
          <Text strong>Create template from current week</Text>
          <div style={{ display: 'flex', gap: 8 }}>
            <Input
              value={templateName}
              placeholder="Template name"
              onChange={(event) => setTemplateName(event.target.value)}
            />
            <Button type="primary" loading={saving} onClick={handleCreate}>Save current week</Button>
          </div>
          <Text type="secondary">
            Source range: {currentWeekRange?.startDate || '-'} to {currentWeekRange?.endDate || '-'}
          </Text>
        </div>

        <div style={{ display: 'flex', gap: 8 }}>
          <div style={{ flex: 1 }}>
            <Text strong>Apply start date</Text>
            <Input type="date" value={applyStartDate} onChange={(event) => setApplyStartDate(event.target.value)} />
          </div>
          <div style={{ flex: 1 }}>
            <Text strong>Apply end date</Text>
            <Input type="date" value={applyEndDate} onChange={(event) => setApplyEndDate(event.target.value)} />
          </div>
        </div>

        <List
          loading={loading}
          dataSource={templates}
          locale={{ emptyText: <Empty description="No templates" /> }}
          renderItem={(template) => (
            <List.Item
              actions={[
                <Button key="apply" type="primary" loading={saving} onClick={() => handleApply(template)}>
                  Apply
                </Button>,
                <Popconfirm
                  key="delete"
                  title="Delete template?"
                  onConfirm={() => handleDelete(template.id)}
                >
                  <Button danger loading={saving}>Delete</Button>
                </Popconfirm>,
              ]}
            >
              <List.Item.Meta
                title={template.templateName}
                description={`Preview shifts: ${previewCounts[template.id] || 0}`}
              />
            </List.Item>
          )}
        />
      </div>
    </Modal>
  );
};

export default RosterTemplateModal;